import { Group as GroupIcon, Person as PersonIcon } from "@mui/icons-material";
import {
	Avatar,
	Box,
	Chip,
	Dialog,
	DialogContent,
	DialogTitle,
	Typography,
} from "@mui/material";
import type { User } from "../types";

interface ParticipantsModalProps {
	open: boolean;
	onClose: () => void;
	users: Record<string, User>;
	participants: string[]; // Array of user IDs
	currentUserId: string;
}

export function ParticipantsModal({
	open,
	onClose,
	users,
	participants,
	currentUserId,
}: ParticipantsModalProps) {
	const sortedParticipants = [...participants]
		.map((id) => users[id])
		.filter(Boolean)
		.sort((a, b) => (a.joinedAt || 0) - (b.joinedAt || 0)); // Sort by join time

	const formatJoinTime = (timestamp: number): string => {
		const now = Date.now();
		const seconds = Math.floor((now - timestamp) / 1000);

		if (seconds < 60) return "just now";
		if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
		if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
		return new Date(timestamp).toLocaleDateString();
	};

	return (
		<Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
			<DialogTitle>
				<Box display="flex" alignItems="center">
					<GroupIcon sx={{ mr: 1, color: "primary.main" }} />
					<Typography variant="h6" sx={{ fontWeight: "bold" }}>
						Participants ({sortedParticipants.length})
					</Typography>
				</Box>
			</DialogTitle>
			<DialogContent>
				<Box sx={{ pt: 1 }}>
					{sortedParticipants.length === 0 ? (
						<Typography
							variant="body2"
							color="text.secondary"
							sx={{ textAlign: "center", py: 3 }}
						>
							No participants yet.
						</Typography>
					) : (
						<Box display="flex" flexDirection="column" gap={2}>
							{sortedParticipants.map((user) => (
								<Box
									key={user.id}
									display="flex"
									alignItems="center"
									sx={{
										p: 2,
										border:
											user.id === currentUserId ? "2px solid" : "1px solid",
										borderColor:
											user.id === currentUserId ? "primary.main" : "grey.300",
										borderRadius: 2,
										backgroundColor:
											user.id === currentUserId ? "primary.50" : "grey.50",
									}}
								>
									<Avatar
										sx={{
											width: 48,
											height: 48,
											mr: 2,
											bgcolor:
												user.id === currentUserId ? "primary.main" : "grey.500",
										}}
									>
										{user.name[0].toUpperCase()}
									</Avatar>
									<Box flexGrow={1}>
										<Typography
											variant="h6"
											sx={{
												fontWeight:
													user.id === currentUserId ? "bold" : "normal",
												color:
													user.id === currentUserId
														? "primary.main"
														: "text.primary",
											}}
										>
											{user.name}
											{user.id === currentUserId && (
												<Chip
													label="You"
													size="small"
													color="primary"
													sx={{ ml: 1, fontSize: "0.7rem" }}
												/>
											)}
										</Typography>
										<Typography variant="body2" color="text.secondary">
											{user.joinedAt
												? `Joined ${formatJoinTime(user.joinedAt)}`
												: "Joined recently"}
										</Typography>
									</Box>
									<PersonIcon sx={{ color: "text.secondary" }} />
								</Box>
							))}
						</Box>
					)}
				</Box>
			</DialogContent>
		</Dialog>
	);
}
